import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { abandonedCheckoutsAPI } from '../services/api';
import './OrderDetails.css';

const formatMoney = (amount, currency = 'AED') =>
  `${currency} ${Number(amount || 0).toFixed(2)}`;

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

function DetailSection({ title, children }) {
  return (
    <div className="order-detail__panel">
      <h3 className="order-detail__section-title">{title}</h3>
      {children}
    </div>
  );
}

function DetailRows({ rows }) {
  return (
    <dl className="order-detail__dl">
      {rows.map(([dt, dd]) => (
        <div key={dt} className="order-detail__row">
          <dt className="order-detail__dt">{dt}</dt>
          <dd className="order-detail__dd">{dd}</dd>
        </div>
      ))}
    </dl>
  );
}

const AbandonedCheckoutDetails = ({ checkout, onDeleted, readOnly = false }) => {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);

  if (!checkout) return null;

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Permanently delete this abandoned cart? This cannot be undone.',
      )
    ) {
      return;
    }

    setDeleting(true);
    try {
      await abandonedCheckoutsAPI.delete(checkout.id);
      toast.success('Abandoned cart deleted.');
      if (typeof onDeleted === 'function') {
        onDeleted();
      } else {
        navigate('/abandoned-checkouts');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || 'Could not delete cart',
      );
    } finally {
      setDeleting(false);
    }
  };

  const summaryRows = [
    ['Started', formatDate(checkout.created_at)],
    ['Expires', formatDate(checkout.expires_at)],
    ['Payment status', checkout.payment_status],
    ...(checkout.is_expired && checkout.payment_status === 'pending'
      ? [['Session', 'Expired']]
      : []),
    ...(checkout.cancelled_at ? [['Cancelled', formatDate(checkout.cancelled_at)]] : []),
    ['Gateway ref', checkout.gateway_order_ref || '—'],
    ['Failure reason', checkout.payment_failure_reason || '—'],
  ];

  const buyerRows = [
    ['Buyer', checkout.buyer_label || 'Guest'],
    ['Email', checkout.buyer?.email || checkout.guest_email || '—'],
  ];

  const totalsRows = [
    ['Subtotal', formatMoney(checkout.subtotal, checkout.currency)],
    ['Shipping', formatMoney(checkout.shipping_fee, checkout.currency)],
    ['Total', formatMoney(checkout.total_price, checkout.currency)],
  ];

  const address = checkout.shipping_address;
  const addressRows = address
    ? [
        ['Name', address.full_name || '—'],
        ['Phone', address.phone_number || '—'],
        [
          'Address',
          [
            address.address_line1,
            address.address_line2,
            address.city,
            address.postal_code,
            address.country,
          ]
            .filter(Boolean)
            .join(', ') || '—',
        ],
      ]
    : [];

  return (
    <div className="order-detail">
      <div className="order-detail__grid">
        <DetailSection title="Checkout">
          <DetailRows rows={summaryRows} />
        </DetailSection>

        <DetailSection title="Buyer">
          <DetailRows rows={buyerRows} />
        </DetailSection>

        <DetailSection title="Totals">
          <DetailRows rows={totalsRows} />
        </DetailSection>

        {address ? (
          <DetailSection title="Shipping address">
            <DetailRows rows={addressRows} />
          </DetailSection>
        ) : null}
      </div>

      <DetailSection title="Cart items">
        {checkout.items?.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {checkout.items.map((item) => (
              <div key={`${item.product_id}-${item.quantity}`} className="order-detail__product-row">
                {item.product?.image_url ? (
                  <img
                    src={item.product.image_url}
                    alt=""
                    className="order-detail__product-thumb"
                  />
                ) : (
                  <div
                    className="order-detail__product-thumb"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94a3b8',
                      fontSize: '12px',
                    }}
                  >
                    No image
                  </div>
                )}
                <div className="order-detail__product-body">
                  <div style={{ fontWeight: 600 }}>{item.product?.title || 'Product'}</div>
                  {item.product?.brand ? (
                    <div className="order-detail__product-desc">{item.product.brand}</div>
                  ) : null}
                  <div className="order-detail__product-desc">
                    Qty {item.quantity} · {formatMoney(item.unit_price, checkout.currency)} each
                  </div>
                  <div style={{ fontWeight: 600, marginTop: '4px' }}>
                    {formatMoney(item.subtotal, checkout.currency)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="order-detail__hint">No line items recorded.</p>
        )}
      </DetailSection>

      {checkout.orders?.length ? (
        <DetailSection title="Linked orders">
          <DetailRows
            rows={checkout.orders.map((order) => [
              order.order_number,
              `${order.status} · ${order.payment_status} · ${formatMoney(order.total_price, checkout.currency)}`,
            ])}
          />
        </DetailSection>
      ) : null}

      {!readOnly && checkout.can_delete !== false ? (
        <div
          className="order-detail__action-card"
          style={{ marginTop: '1.25rem', borderColor: '#fecaca', background: '#fff5f5' }}
        >
          <h3 className="order-detail__section-title" style={{ color: '#991b1b' }}>
            Danger zone
          </h3>
          <p className="order-detail__hint" style={{ marginBottom: '12px' }}>
            Remove this abandoned cart from the admin list. Only incomplete checkouts can be deleted.
          </p>
          <button
            type="button"
            className="btn btn-danger"
            disabled={deleting}
            onClick={() => void handleDelete()}
          >
            {deleting ? 'Deleting…' : 'Delete cart'}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default AbandonedCheckoutDetails;
